import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ShieldCheck, Loader2, Plus, X, CheckCircle2, AlertTriangle, CircleSlash } from "lucide-react";

const STATUS = {
  ok: { icon: CheckCircle2, cls: "text-emerald-600", label: "OK" },
  expired: { icon: AlertTriangle, cls: "text-amber-600", label: "Expired" },
  missing: { icon: CircleSlash, cls: "text-red-500", label: "Missing" },
};

function scoreColor(s) {
  if (s >= 85) return "#16a34a";
  if (s >= 60) return "#d97706";
  return "#dc2626";
}

export function ComplianceCard({ participant, refreshKey }) {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [types, setTypes] = useState([]);
  const [addRef, setAddRef] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.participantCompliance(participant).then(setData).catch(() => toast.error("Could not load compliance."));
  useEffect(() => { load(); api.complianceTypes().then((d) => setTypes(d.types)).catch(() => {}); /* eslint-disable-next-line */ }, [participant, refreshKey]);

  const requiredRefs = useMemo(() => new Set((data?.items || []).map((i) => i.ref)), [data]);
  const available = types.filter((t) => !requiredRefs.has(t.ref));

  const add = async () => {
    if (!addRef) return;
    setBusy(true);
    try { setData(await api.addRequirement(participant, addRef)); setAddRef(""); toast.success("Requirement added."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); } finally { setBusy(false); }
  };
  const remove = async (ref) => {
    setBusy(true);
    try { setData(await api.removeRequirement(participant, ref)); toast.success("Requirement removed."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); } finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const col = scoreColor(data.score);

  return (
    <div className="mt-4 space-y-4" data-testid="compliance-card">
      <div className="rounded-xl border border-slate-200 bg-white p-5 flex items-center gap-5">
        <div className="relative h-20 w-20 shrink-0">
          <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke={col} strokeWidth="3" strokeLinecap="round"
              strokeDasharray={`${data.score} 100`} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center flex-col">
            <span className="text-lg font-bold" style={{ color: col }} data-testid="compliance-score">{data.score}</span>
            <span className="text-[8px] text-slate-400 -mt-1">/ 100</span>
          </div>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" style={{ color: col }} />Compliance score</div>
          <p className="text-[11px] text-slate-500 mt-0.5 max-w-md">Weighted by each document's importance. A missing or expired required document lowers the score in proportion to how important it is.</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Required documents</div>
        <div className="divide-y divide-slate-50">
          {data.items.map((it) => {
            const st = STATUS[it.status] || STATUS.missing;
            const Icon = st.icon;
            return (
              <div key={it.ref} className="flex items-center gap-3 py-2" data-testid={`req-${it.ref}`}>
                <Icon className={`h-4 w-4 shrink-0 ${st.cls}`} />
                <span className="text-sm text-slate-700 flex-1 truncate">
                  {it.name}
                  {it.mandatory && <Badge className="ml-2 text-[9px] py-0 bg-slate-100 text-slate-500 border-slate-200">Mandatory</Badge>}
                  {!it.configured && <Badge className="ml-2 text-[9px] py-0 bg-amber-50 text-amber-600 border-amber-200">Not configured</Badge>}
                </span>
                <span className="text-[10px] text-slate-400">imp {it.importance}</span>
                <span className={`text-[10px] font-medium ${st.cls} w-14 text-right`}>{st.label}</span>
                {can.has("participant_docs.edit") && !it.mandatory && (
                  <button onClick={() => remove(it.ref)} disabled={busy} className="text-slate-300 hover:text-rose-600 p-1" title="Remove requirement" data-testid={`req-remove-${it.ref}`}><X className="h-4 w-4" /></button>
                )}
                {(it.mandatory || !can.has("participant_docs.edit")) && <span className="w-6" />}
              </div>
            );
          })}
        </div>
        {can.has("participant_docs.edit") && available.length > 0 && (
          <div className="flex items-end gap-2 pt-2">
            <div className="flex-1">
              <Select value={addRef} onValueChange={setAddRef}>
                <SelectTrigger className="h-9 text-sm" data-testid="req-add-select"><SelectValue placeholder="Add a required document…" /></SelectTrigger>
                <SelectContent>
                  {available.map((t) => <SelectItem key={t.ref} value={t.ref} className="text-sm">{t.name} <span className="text-slate-400">· imp {t.importance}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={add} disabled={busy || !addRef} variant="outline" data-testid="req-add-btn"><Plus className="h-4 w-4 mr-1.5" />Require</Button>
          </div>
        )}
      </div>
    </div>
  );
}
